import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react';
import type { ReactElement, SVGProps } from 'react';
import { cn } from '@/lib/cn';

/**
 * Tamaños del sistema de diseño (docs/design/martech-design-system.md → Íconos): 24 es el del chrome. Los menores son para texto
 * en línea, botones compactos y chips. En la práctica casi todo el panel dimensiona con `className="size-4"` (la clase gana al
 * atributo `width/height` del SVG); `size` es para los casos sin clase.
 */
export const ICON_SIZES = { xs: 12, sm: 16, md: 20, lg: 24, xl: 40 } as const;
export type IconSize = keyof typeof ICON_SIZES;

/** Grosor del trazo: por omisión el propio del ícono (1,5, contorno redondeado de Hugeicons). */
export const ICON_STROKES = { light: 1.25, regular: 1.5, bold: 2 } as const;
export type IconStroke = keyof typeof ICON_STROKES;

/** Color por tokens (nunca hex): `inherit` hereda `currentColor`. Los íconos son de contorno y nunca van rellenos. */
const TONES = {
  inherit: '',
  foreground: 'text-foreground',
  muted: 'text-muted-foreground',
  primary: 'text-primary',
  destructive: 'text-destructive',
} as const;
export type IconTone = keyof typeof TONES;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'stroke' | 'color' | 'width' | 'height'> {
  size?: IconSize | number;
  /** Token (`light`/`regular`/`bold`) o número (compatibilidad con `strokeWidth`). */
  stroke?: IconStroke;
  strokeWidth?: number;
  tone?: IconTone;
  /**
   * Nombre accesible. Sin él, el ícono es decorativo (`aria-hidden`): el texto del botón o del enlace ya lo nombra. Con él (o con
   * `aria-label`) pasa a `role="img"`. Un ícono suelto que transmite estado SIEMPRE debe llevarlo.
   */
  title?: string;
}

/** Componente de un ícono de la capa: `<Plus className="size-4" />`. */
export type IconComponent = ((props: IconProps) => ReactElement) & { displayName?: string };

/**
 * Ícono base de la capa (`components/icons`): envuelve un glifo de `@hugeicons/core-free-icons` (MIT). No lo uses directo en
 * pantallas: importa el ícono semántico desde `@/components/icons` (p. ej. `Plus`, `Trash2`).
 */
export function Icon({
  glyph,
  size = 'lg',
  stroke,
  strokeWidth,
  tone = 'inherit',
  title,
  className,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
  ...rest
}: IconProps & { glyph: IconSvgElement }) {
  const label = ariaLabel ?? title;
  const width = strokeWidth ?? (stroke ? ICON_STROKES[stroke] : undefined);
  return (
    <HugeiconsIcon
      icon={glyph}
      size={typeof size === 'number' ? size : ICON_SIZES[size]}
      {...(width === undefined ? {} : { strokeWidth: width })}
      className={cn('shrink-0', TONES[tone], className)}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': ariaHidden ?? true })}
      {...rest}
    />
  );
}

/** Fabrica un ícono semántico a partir de un glifo. Anotado `PURE` para que el empaquetador pode los no usados. */
export function createIcon(glyph: IconSvgElement, name: string): IconComponent {
  const Component: IconComponent = (props) => <Icon glyph={glyph} {...props} />;
  Component.displayName = name;
  return Component;
}
